import { AutoProfileConstants } from "@/constants/autoProfileEnum";
import { ConicalType } from "@/constants/autoProfileOption";
import {
  BUFFER_EXCHANGE_SENSOR,
  CONCENTRATE_SENSOR,
  NO_LIMIT,
  STARTING_VOLUME_DIFFERENCE,
  VOLUME_SENSOR,
} from "@/constants/configurations";
import { MembraneTypeEnum } from "@/constants/dataEnum";
import { MORE_THAN_50_ML, RECOVERY_OF_LIQUID } from "@/constants/enums";
import {
  AUTO_RECOVERY,
  BUFFER_EXCHANGE,
  CONC_TWO_TUBES,
  CONCENTRATE,
  CONTINUOUS_DIAFILTRATION,
  FINAL_VOLUME,
  STARTING_VOLUME,
  STEP_SIZE,
  VOLUME_TO_START_AT,
} from "@/constants/fieldNames";
import {
  CurrentPressure,
  PumpFlowRate,
  CurrentVolume,
  EstimateConcentration,
  EstSecondRemaining,
  SecondElapsed,
  WarningMessage,
  CurrentBuffer,
  FailureMessage,
  ProfileRunnig,
  ConcentrationFlowRate,
  CurrentPressureSensors,
  CurrentVolumeChangesThreshold,
  ErrorVolumeChanges,
  TransferVolume,
  CurrentTiming,
  VolumeToStartAt,
  BufferVolumeToUse,
  TotalBufferVolumeToUse,
  ExchangeStepSize,
  StepSizeVolume,
  REQUIRE_REFILL,
} from "@/constants/jsonKeys";
import {
  BUFFER_THRESHOLD,
  EMPTY_FIELD_NUMERIC,
  MAX_TUBE_VOLUME,
} from "@/constants/pcConstAppData";

import { isEmptyFieldValue, isInt } from "./appHelper";

export const getAutoProfileUpdateStatusModel = (data) => ({
  currentPressure: data[CurrentPressure],
  pumpFlowRate: data[PumpFlowRate],
  currentVolume: data[CurrentVolume],
  estimateConcentration: data[EstimateConcentration],
  estSecondsRemaining: data[EstSecondRemaining],
  secondsElapsed: data[SecondElapsed],
  warningMessage: data[WarningMessage],
  currentBuffer: data[CurrentBuffer],
  failureMessage: data[FailureMessage],
  profileRunning: data[ProfileRunnig],
  concentrationFlowRate: data[ConcentrationFlowRate],
  currentPressureSensors: data[CurrentPressureSensors],
  currentVolumeChangesThreshold: data[CurrentVolumeChangesThreshold],
  ErrorVolumeChanges: data[ErrorVolumeChanges],
  currentTiming: data[CurrentTiming],
  transferVolume: data[TransferVolume],
  requireRefill: data[REQUIRE_REFILL],
});

export const getAutoProfileModeOnRunningProfile = (
  DiafiltrationMode,
  MoreThan50ml
) => {
  const diafiltrationMode = String(DiafiltrationMode);
  const moreThan50ml = String(MoreThan50ml);

  if (moreThan50ml === MORE_THAN_50_ML.ON) return CONC_TWO_TUBES;

  if (diafiltrationMode === CONTINUOUS_DIAFILTRATION) return BUFFER_EXCHANGE;

  return CONCENTRATE;
};

export const generateProfileConfigModel = (profile) => {
  const result = { ...profile };

  // eslint-disable-next-line no-restricted-syntax
  for (const key in result) {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      const value = result[key];

      if (!value) result[key] = EMPTY_FIELD_NUMERIC.toString();
    }
  }

  return result;
};

export const membraneTypeConverter = (type) => {
  switch (type) {
    case 1:
    case "1":
    case MembraneTypeEnum.MPES:
      return 1;
    case 2:
    case "2":
    case MembraneTypeEnum.RC:
      return 2;
    default:
      return null;
  }
};

// #region CONCENTRATE

export const parseNumberParam = (value) =>
  Number(isEmptyFieldValue(value) ? null : value);

export const calcEstFinalConcentrationConcentrate = (
  _startingVol,
  _finalVol,
  _startingConcentration,
  _fixedNum = VOLUME_SENSOR.DECIMAL_PLACE_ACCURACY
) => {
  const startingVol = Number(_startingVol);
  const finalVol = Number(_finalVol);
  const startingConcentration = Number(_startingConcentration);
  let fixedNum = Number(_fixedNum);

  if (startingConcentration < 1) fixedNum = CONCENTRATE_SENSOR.FIXED_VALUE;

  const result = (startingVol / finalVol) * startingConcentration;

  if (isInt(result)) return result;

  return Number(+result.toFixed(fixedNum));
};

export const calcFinalVolumeConcentrate = (
  _startingVol,
  _estFinalConcentration,
  _startingConcentration,
  _fixedNum = VOLUME_SENSOR.DECIMAL_PLACE_ACCURACY
) => {
  const startingVol = Number(_startingVol);
  const estFinalConcentration = Number(_estFinalConcentration);
  const startingConcentration = Number(_startingConcentration);
  let fixedNum = Number(_fixedNum);

  if (startingConcentration < 1)
    fixedNum = CONCENTRATE_SENSOR.DECIMAL_PLACE_ACCURACY;

  const result = (startingVol / estFinalConcentration) * startingConcentration;

  if (isInt(result)) return result;

  return Number(+result.toFixed(fixedNum));
};

// #endregion

// #region CONCENTRATE 2 TUBES

export const calcTotalStartingVolConcentrate2Tubes = (
  _startingLeftVol,
  _startingRightVol
) => {
  const startingLeftVol = Number(_startingLeftVol);
  const startingRightVol = Number(_startingRightVol);
  const result = startingLeftVol + startingRightVol;

  return result;
};

export const calcEstFinalConcentrationConcentrate2Tubes = (
  _startingLeftVol,
  _startingRightVol,
  _finalVol,
  _startingConcentration,
  _fixedNum = VOLUME_SENSOR.DECIMAL_PLACE_ACCURACY
) => {
  const totalStartVolume = calcTotalStartingVolConcentrate2Tubes(
    _startingLeftVol,
    _startingRightVol
  );
  const finalVol = Number(_finalVol);
  const startingConcentration = Number(_startingConcentration);
  let fixedNum = Number(_fixedNum);

  if (startingConcentration < 1) fixedNum = CONCENTRATE_SENSOR.FIXED_VALUE;

  const result = (totalStartVolume / finalVol) * startingConcentration;

  if (isInt(result)) return result;

  return Number(+result.toFixed(fixedNum));
};

export const calcFinalVolumeConcentrate2Tubes = (
  _startingLeftVol,
  _startingRightVol,
  _estFinalConcentration,
  _startingConcentration,
  _fixedNum = VOLUME_SENSOR.DECIMAL_PLACE_ACCURACY
) => {
  const totalStartVolume = calcTotalStartingVolConcentrate2Tubes(
    _startingLeftVol,
    _startingRightVol
  );
  const estFinalConcentration = Number(_estFinalConcentration);
  const startingConcentration = Number(_startingConcentration);
  let fixedNum = Number(_fixedNum);

  if (startingConcentration < 1)
    fixedNum = CONCENTRATE_SENSOR.DECIMAL_PLACE_ACCURACY;

  const result =
    (totalStartVolume / estFinalConcentration) * startingConcentration;

  if (isInt(result)) return result;

  return Number(+result.toFixed(fixedNum));
};

export const getFinalVolumeConcentrate2TubesMaxValue = (
  startVolumeLeft,
  startVolumeRight,
  conicalVol
) => {
  const totalStartVolume = calcTotalStartingVolConcentrate2Tubes(
    startVolumeLeft,
    startVolumeRight
  );
  const conicalVolume = Number(conicalVol || ConicalType[50]);

  let maxValue = conicalVolume;

  if (totalStartVolume >= conicalVolume + STARTING_VOLUME_DIFFERENCE) {
    maxValue = conicalVolume;
  } else if (totalStartVolume < conicalVolume + STARTING_VOLUME_DIFFERENCE) {
    maxValue = totalStartVolume - STARTING_VOLUME_DIFFERENCE;
  }

  return maxValue;
};

// #endregion

// #region BUFFER EXCHANGE

export const calcEstFinalConcentrationBufferExchange = (
  _startingVol,
  _finalVol,
  _startingConcentration,
  _fixedNum = VOLUME_SENSOR.DECIMAL_PLACE_ACCURACY
) => {
  const startingVol = Number(_startingVol);
  const finalVol = Number(_finalVol);
  const startingConcentration = Number(_startingConcentration);
  let fixedNum = Number(_fixedNum);

  if (startingConcentration < 1) fixedNum = CONCENTRATE_SENSOR.FIXED_VALUE;

  const result = (startingVol / finalVol) * startingConcentration;

  if (isInt(result)) return result;

  return Number(+result.toFixed(fixedNum));
};

export const calcFinalVolumeBufferExchange = (
  _startingVol,
  _estFinalConcentration,
  _startingConcentration,
  _fixedNum = VOLUME_SENSOR.DECIMAL_PLACE_ACCURACY
) => {
  const startingVol = Number(_startingVol);
  const estFinalConcentration = Number(_estFinalConcentration);
  const startingConcentration = Number(_startingConcentration);
  let fixedNum = Number(_fixedNum);

  if (startingConcentration < 1)
    fixedNum = CONCENTRATE_SENSOR.DECIMAL_PLACE_ACCURACY;

  const result = (startingVol / estFinalConcentration) * startingConcentration;

  if (isInt(result)) return result;

  return Number(+result.toFixed(fixedNum));
};

export const calcVolumeToStartAtBufferExchange = (
  _startingVol,
  _finalVol,
  _conicalVolume = ConicalType[50]
) => {
  const startingVol = Number(_startingVol);
  const finalVol = Number(_finalVol);
  const conicalVolume = Number(_conicalVolume);
  let result =
    finalVol +
    AutoProfileConstants.VOLUME_DIFFERENT_MULTIPLIER * (startingVol - finalVol);

  if (result >= conicalVolume - VOLUME_SENSOR.MIN_STEP_SIZE)
    result = conicalVolume - VOLUME_SENSOR.MIN_STEP_SIZE;

  return Number(+result.toFixed(2));
};

export const calcBufferVolumeToUseBufferExchange = (
  _startingVol,
  _finalVol,
  recoveryOfLiquid,
  _conicalVolume = ConicalType[50]
) => {
  const conicalVolume = Number(_conicalVolume);
  const volumeToStartAt = Number(
    calcVolumeToStartAtBufferExchange(_startingVol, _finalVol, conicalVolume)
  );
  const maximumBuffer =
    recoveryOfLiquid === RECOVERY_OF_LIQUID.Buffer ||
    recoveryOfLiquid === RECOVERY_OF_LIQUID.Air_Buffer
      ? MAX_TUBE_VOLUME - BUFFER_THRESHOLD
      : MAX_TUBE_VOLUME;

  const result = maximumBuffer - volumeToStartAt;

  return Number(+result.toFixed(1));
};

export const calcTotalBufferVolumeToUseBufferExchange = (
  _startingVol,
  _finalVol,
  recoveryOfLiquid,
  _conicalVolume = ConicalType[50]
) => {
  const conicalVolume = Number(_conicalVolume);
  const bufferVolumeToUse = Number(
    calcBufferVolumeToUseBufferExchange(
      _startingVol,
      _finalVol,
      recoveryOfLiquid,
      conicalVolume
    )
  );

  const result = bufferVolumeToUse + BUFFER_THRESHOLD;

  // if (
  //   parseFloat(currentProfileParams[APPDATA.KEY.TotalBufferVolumeToUse]) <= MAX_TUBE_VOLUME - BUFFER_THRESHOLD &&
  //   (recoveryOfLiquid === RECOVERY_OF_LIQUID.Buffer || recoveryOfLiquid === RECOVERY_OF_LIQUID.Air_Buffer)
  // ) {
  //   result = bufferVolumeToUse + BUFFER_THRESHOLD;
  // }

  // if (conicalVolume === ConicalType[15].toString()) {
  //   result = EMPTY_FIELD_NUMERIC;
  // }

  return Number(+result.toFixed(1));
};

export const calculateStepSizeInBufferExchange = (
  _volumeToStartAt,
  bufferVolumeToUse,
  conicalVolume
) => {
  const volumeToStartAt = parseFloat(_volumeToStartAt);
  const bufferToUse = parseFloat(bufferVolumeToUse);
  const stepSize = BUFFER_EXCHANGE_SENSOR.DEFAULT_STEP_SIZE;

  let result = BUFFER_EXCHANGE_SENSOR.DEFAULT_STEP_SIZE;

  if (volumeToStartAt > conicalVolume - stepSize) {
    const remainingVolume = conicalVolume - volumeToStartAt;

    if (bufferToUse < remainingVolume) {
      result = bufferToUse;
    } else {
      result = remainingVolume;
    }
  } else if (volumeToStartAt <= conicalVolume - stepSize) {
    if (bufferToUse < 1) {
      result = bufferToUse;
    } else {
      result = stepSize;
    }
  }

  return result;
};

export const calcExchangeSizeBufferExchange = (
  _bufferVolumeToUse,
  _volumeToStartAt,
  _conicalVolume = ConicalType[50]
) => {
  const bufferVolumeToUse = Number(_bufferVolumeToUse);
  const volumeToStartAt = Number(_volumeToStartAt);
  const conicalVolume = Number(_conicalVolume);

  const result = calculateStepSizeInBufferExchange(
    volumeToStartAt,
    bufferVolumeToUse,
    conicalVolume
  );

  return Number(result.toFixed(2));
};

export const calcBufferExchangeParams = (
  _startingVol,
  _finalVol,
  recoveryOfLiquid,
  _conicalVolume = ConicalType[50]
) => {
  const volumeToStartAt = calcVolumeToStartAtBufferExchange(
    _startingVol,
    _finalVol,
    _conicalVolume
  );
  const bufferVolumeToUse = calcBufferVolumeToUseBufferExchange(
    _startingVol,
    _finalVol,
    recoveryOfLiquid,
    _conicalVolume
  );
  const totalBufferVolumeToUse = Number(
    calcTotalBufferVolumeToUseBufferExchange(
      _startingVol,
      _finalVol,
      recoveryOfLiquid,
      _conicalVolume
    )
  );

  const exchangeStepSize = Number(
    calcExchangeSizeBufferExchange(
      bufferVolumeToUse,
      volumeToStartAt,
      _conicalVolume
    ).toFixed(2)
  );

  return {
    [VolumeToStartAt]: volumeToStartAt,
    [BufferVolumeToUse]: bufferVolumeToUse,
    [TotalBufferVolumeToUse]: totalBufferVolumeToUse,
    [ExchangeStepSize]: exchangeStepSize,
    [StepSizeVolume]: exchangeStepSize,
  };
};

export const validateBufferVolBufferExchange = (
  _totalBufferVolumeToUse,
  _bufferVolumeToUse
) => {
  const totalBufferVolumeToUse = Number(_totalBufferVolumeToUse);
  const bufferVolumeToUse = Number(_bufferVolumeToUse);

  let bufferVolume = bufferVolumeToUse;

  if (
    bufferVolumeToUse &&
    totalBufferVolumeToUse < bufferVolumeToUse + BUFFER_THRESHOLD
  ) {
    bufferVolume = bufferVolumeToUse + BUFFER_THRESHOLD;
  }

  return bufferVolume;
};

export const validateExchangeStepSize = (
  _exchangeStepSize,
  _volumeToStartAt,
  _conicalVolume = ConicalType[50]
) => {
  const exchangeStepSize = Number(_exchangeStepSize);
  const volumeToStartAt = Number(_volumeToStartAt);
  const conicalVolume = Number(_conicalVolume);

  let result = exchangeStepSize;
  if (volumeToStartAt + exchangeStepSize >= conicalVolume)
    result = conicalVolume - volumeToStartAt;

  return result;
};

// #endregion

// #region BUFFER EXCHANGE CALCULATOR

export const calcExchangeCalculator = (
  volumeToStartAt,
  percentage,
  stepSize
) => {
  // Dia Vol = Step * log (1/ (1-Ex%)) / log( (Vol+Step) / Vol)
  let exchangeCountCalculator = 0;

  if (
    !isEmptyFieldValue(volumeToStartAt) &&
    !isEmptyFieldValue(percentage) &&
    !isEmptyFieldValue(stepSize)
  ) {
    exchangeCountCalculator =
      Math.log(1 / (1 - parseFloat(percentage) / 100)) /
      Math.log(
        (parseFloat(volumeToStartAt) + parseFloat(stepSize)) /
          parseFloat(volumeToStartAt)
      );
  }

  return exchangeCountCalculator;
};

export const calcMinExchangeCalculator = (exchangeVolume, volumeToStartAt) => {
  let minExchangeCount = 1;
  if (
    !isEmptyFieldValue(exchangeVolume) &&
    !isEmptyFieldValue(volumeToStartAt) &&
    volumeToStartAt !== null &&
    volumeToStartAt !== 0 &&
    volumeToStartAt !== BUFFER_EXCHANGE_SENSOR.MAX_VALUE
  ) {
    const restVolume =
      BUFFER_EXCHANGE_SENSOR.MAX_VALUE - parseFloat(volumeToStartAt);
    minExchangeCount = parseFloat(exchangeVolume) / restVolume;
    if (
      parseFloat(exchangeVolume) /
        BUFFER_EXCHANGE_SENSOR.MIN_VOLUME_IN_ONE_EXCHANGE <
      minExchangeCount
    ) {
      minExchangeCount =
        parseFloat(exchangeVolume) /
        BUFFER_EXCHANGE_SENSOR.MIN_VOLUME_IN_ONE_EXCHANGE;
    }
  }

  if (Math.ceil(minExchangeCount) < 0.5) {
    return 0.5;
  }

  return Math.ceil(minExchangeCount);
};

export const calcMaxStepSizeCalculator = (exchangeVolume, volumeToStartAt) => {
  const excVol = Number(exchangeVolume) > 0 ? Number(exchangeVolume) : 0;
  const numberVolume = Number(volumeToStartAt);

  if (numberVolume + excVol <= 50) {
    return excVol;
  }

  return Math.ceil(50 - numberVolume);
};

export const calcBufferNeededCalculator = (
  volumeToStartAt,
  percentage,
  stepSize
) => {
  // Dia Vol = Step * log (1/ (1-Ex%)) / log( (Vol+Step) / Vol)
  const exchangeCountCalculator = calcExchangeCalculator(
    volumeToStartAt,
    percentage,
    stepSize
  );
  const diafiltrationVolume =
    parseFloat(stepSize) * parseFloat(exchangeCountCalculator);

  return Math.ceil(parseFloat(diafiltrationVolume));
};

// #endregion

// #region LIMIT PARAMS

export const getLimitStartVol = (_conicalVolume = ConicalType[50]) => ({
  max: Number(_conicalVolume ?? ConicalType[50]),
  min: AutoProfileConstants.MIN_STARTING_VOL,
});

export const getLimitStartVolConc = (_conicalVolume = ConicalType[50]) => ({
  max: Number(_conicalVolume ?? ConicalType[50]),
  min: AutoProfileConstants.MIN_STARTING_VOL,
});

export const getLimitStartingVol1stTubeConc2Tubes = (
  _conicalVolume = ConicalType[50]
) => ({
  max: Number(_conicalVolume ?? ConicalType[50]),
  min: AutoProfileConstants.MIN_STARTING_VOL,
});

export const getLimitStartingVol2ndTubeConc2Tubes = () => ({
  max: NO_LIMIT,
  min: AutoProfileConstants.MIN_STARTING_VOL,
});

export const getLimitStartConcentration = () => ({
  max: NO_LIMIT,
  min: CONCENTRATE_SENSOR.MIN_VALUE,
});

export const getLimitFinalVolConc = (
  _startingVolume,
  _conicalVolume = ConicalType[50]
) => {
  const startingVolume = Number(_startingVolume);
  const conicalVolume = Number(_conicalVolume ?? ConicalType[50]);

  const min = AutoProfileConstants.MIN_FINAL_VOL;
  let max = startingVolume - STARTING_VOLUME_DIFFERENCE;

  if (startingVolume >= conicalVolume)
    max = conicalVolume - STARTING_VOLUME_DIFFERENCE;

  return {
    max,
    min,
  };
};

export const getLimitFinalVolConc2Tubes = (
  _tube1,
  _tube2,
  _conicalVolume = ConicalType[50]
) => {
  const min = AutoProfileConstants.MIN_FINAL_VOL;
  const max = getFinalVolumeConcentrate2TubesMaxValue(
    _tube1,
    _tube2,
    _conicalVolume
  );

  return {
    max,
    min,
  };
};

export const getLimitFinalVolBufferExchange = (
  _startingVolume,
  _conicalVolume = ConicalType[50]
) => {
  const startingVolume = Number(_startingVolume);
  const conicalVolume = Number(_conicalVolume ?? ConicalType[50]);

  const min = AutoProfileConstants.MIN_FINAL_VOL;
  let max = startingVolume;

  if (startingVolume >= conicalVolume - STARTING_VOLUME_DIFFERENCE)
    max = conicalVolume - STARTING_VOLUME_DIFFERENCE;

  return {
    max,
    min,
  };
};

export const getLimitFinalConcentrationConc = (
  _startingVolume,
  _startingConcentration
) => {
  const startingVolume = Number(_startingVolume);
  const startingConcentration = Number(_startingConcentration);

  const min =
    Number(
      +(
        (startingVolume * startingConcentration) /
        (startingVolume - STARTING_VOLUME_DIFFERENCE)
      ).toFixed(2)
    ) || CONCENTRATE_SENSOR.MIN_VALUE;
  const max =
    (startingVolume * startingConcentration) /
    AutoProfileConstants.MIN_FINAL_VOL;

  return {
    max,
    min,
  };
};

export const getLimitFinalConcentrationConc2Tubes = (
  _tube1,
  _tube2,
  _startingConcentration,
  _conicalVolume = ConicalType[50]
) => {
  const tube1 = Number(_tube1);
  const tube2 = Number(_tube2);
  const conicalVolume = Number(_conicalVolume ?? ConicalType[50]);
  const startingConcentration = Number(_startingConcentration);

  const max =
    (startingConcentration * (tube1 + tube2)) /
    AutoProfileConstants.MIN_FINAL_VOL;
  let min = CONCENTRATE_SENSOR.MIN_VALUE;

  if (tube1 + tube2 >= conicalVolume)
    min = (startingConcentration * (tube1 + tube2)) / conicalVolume;
  else
    min =
      ((tube1 + tube2) * startingConcentration) /
      (tube1 + tube2 - STARTING_VOLUME_DIFFERENCE);

  return {
    max,
    min,
  };
};

export const getLimitFinalConcentrationBufferExchange = (
  _startingVolume,
  _startingConcentration,
  _conicalVolume = ConicalType[50]
) => {
  const startingVolume = Number(_startingVolume);
  const startingConcentration = Number(_startingConcentration);
  const maxFill = Number(_conicalVolume) - STARTING_VOLUME_DIFFERENCE;

  const min =
    startingVolume < maxFill
      ? startingConcentration
      : (startingConcentration * startingVolume) / maxFill;
  const max =
    (startingVolume * startingConcentration) /
    AutoProfileConstants.MIN_FINAL_VOL;

  return {
    max,
    min,
  };
};

export const getLimitVolToStartAtBufferExchange = (
  _startingVolume,
  _finalVolume,
  _conicalVolume = ConicalType[50]
) => {
  const conicalVolume = Number(_conicalVolume ?? ConicalType[50]);
  const startingVolume = Number(_startingVolume);
  const finalVolume = Number(_finalVolume);

  let max;
  const min = Number(finalVolume);

  if (startingVolume === conicalVolume)
    max = conicalVolume - STARTING_VOLUME_DIFFERENCE;
  else if (startingVolume < conicalVolume) max = startingVolume;

  return {
    max,
    min,
  };
};

export const getLimitBufferVolToUseBufferExchange = () => ({
  max: VOLUME_SENSOR.MAX_EXCHANGE_VOLUME,
  min: VOLUME_SENSOR.MIN_STEP_SIZE,
});

export const getMaxStepSizeBufferExchange = (
  _bufferVolumeToUse,
  _volumeToStartAt,
  _conicalVolume = ConicalType[50]
) => {
  const bufferVolumeToUse = Number(_bufferVolumeToUse);
  const conicalVolume = Number(_conicalVolume ?? ConicalType[50]);
  const volumeToStartAt = Number(_volumeToStartAt);

  let result = conicalVolume;

  // legacy
  // if (bufferVolumeToUse + volumeToStartAt <= Number(conicalVolume || ConicalType[50]))
  //   maxValue = bufferVolumeToUse;
  // else maxValue = Number(conicalVolume || ConicalType[50]) - volumeToStartAt;

  if (bufferVolumeToUse <= conicalVolume - volumeToStartAt)
    result = bufferVolumeToUse;
  else if (bufferVolumeToUse > conicalVolume - volumeToStartAt)
    result = conicalVolume - volumeToStartAt;

  const validateParsedResult = Number(result.toFixed(2));

  return validateParsedResult;
};

export const getLimitStepSizeBufferExchange = (
  _bufferVolumeToUse,
  _volumeToStartAt,
  _conicalVolume = ConicalType[50]
) => ({
  max: getMaxStepSizeBufferExchange(
    _bufferVolumeToUse,
    _volumeToStartAt,
    _conicalVolume
  ),
  min: VOLUME_SENSOR.MIN_STEP_SIZE,
});

export const getLimitTotalBufferVolToUseBufferExchange = () => ({
  max: NO_LIMIT,
  min: VOLUME_SENSOR.MIN_VALUE + 2,
});

export const getInvalidFieldConc = (
  _startingVolume,
  _finalVolume,
  autoRecovery,
  _conicalVolume = ConicalType[50]
) => {
  const startingVolume = Number(_startingVolume);
  const finalVolume = Number(_finalVolume);
  const conicalVolume = Number(_conicalVolume);

  const invalidFields = [];

  const { max: maxStartVol, min: minStartVol } =
    getLimitStartVolConc(conicalVolume);

  if (startingVolume < minStartVol || startingVolume > maxStartVol) {
    invalidFields.push(STARTING_VOLUME);
  }

  const { max: maxFinalVol, min: minFinalVol } = getLimitFinalVolConc(
    startingVolume,
    conicalVolume
  );

  if (finalVolume < minFinalVol || finalVolume > maxFinalVol) {
    invalidFields.push(FINAL_VOLUME);
  }
  if (autoRecovery && finalVolume < 2 && finalVolume !== 0) {
    invalidFields.push(AUTO_RECOVERY);
  }
  return invalidFields;
};

export const getInvalidFieldConc2Tubes = (
  _startingVolume,
  _startingVolume2nd,
  _finalVolume,
  autoRecovery,
  _conicalVolume = ConicalType[50]
) => {
  const startingVolume = Number(_startingVolume);
  const finalVolume = Number(_finalVolume);
  const startingVolume2nd = Number(_startingVolume2nd);
  const conicalVolume = Number(_conicalVolume);

  const invalidFields = [];

  const { max: maxStartVol, min: minStartVol } =
    getLimitStartVol(conicalVolume);

  if (startingVolume < minStartVol || startingVolume > maxStartVol) {
    invalidFields.push(STARTING_VOLUME);
  }

  const { max: maxFinalVol, min: minFinalVol } = getLimitFinalVolConc2Tubes(
    startingVolume,
    startingVolume2nd,
    conicalVolume
  );

  if (finalVolume < minFinalVol || finalVolume > maxFinalVol) {
    invalidFields.push(FINAL_VOLUME);
  }

  if (autoRecovery && finalVolume < 2 && finalVolume !== 0) {
    invalidFields.push(AUTO_RECOVERY);
  }

  return invalidFields;
};

export const getInvalidFieldBufferExchange = (
  _startingVolume,
  _finalVolume,
  _volumeToStartAt,
  _stepSizeVolume,
  _bufferVolumeToUse,
  autoRecovery,
  _conicalVolume
) => {
  const startingVolume = Number(_startingVolume);
  const finalVolume = Number(_finalVolume);
  const volumeToStartAt = Number(_volumeToStartAt);
  const stepSizeVolume = Number(_stepSizeVolume);
  const bufferVolumeToUse = Number(_bufferVolumeToUse);
  const conicalVolume = Number(_conicalVolume);

  const invalidFields = [];

  const { max: maxStartVol, min: minStartVol } =
    getLimitStartVol(conicalVolume);

  if (startingVolume < minStartVol || startingVolume > maxStartVol) {
    invalidFields.push(STARTING_VOLUME);
  }

  const { max: maxFinalVol, min: minFinalVol } = getLimitFinalVolBufferExchange(
    startingVolume,
    conicalVolume
  );

  if (finalVolume < minFinalVol || finalVolume > maxFinalVol) {
    invalidFields.push(FINAL_VOLUME);
  }

  const { max: maxStartExchange, min: minStartExchange } =
    getLimitVolToStartAtBufferExchange(
      startingVolume,
      finalVolume,
      conicalVolume
    );

  if (
    volumeToStartAt < minStartExchange ||
    volumeToStartAt > maxStartExchange
  ) {
    invalidFields.push(VOLUME_TO_START_AT);
  }

  const { max: maxStepSize, min: minStepSize } = getLimitStepSizeBufferExchange(
    bufferVolumeToUse,
    volumeToStartAt,
    conicalVolume
  );

  if (stepSizeVolume < minStepSize || stepSizeVolume > maxStepSize) {
    invalidFields.push(STEP_SIZE);
  }

  if (autoRecovery && finalVolume < 2 && finalVolume !== 0) {
    invalidFields.push(AUTO_RECOVERY);
  }

  return invalidFields;
};

// #endregion
